import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { formatMessage } from '../../translations/format-message';
import { useStore } from '../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import { IDetailProps } from '../../models/forms/IForm';
import { IProductVM } from '../../models/response/IProductResponse';
import { fixTimezoneOffsetYYYYMMDD, minDate } from '../../constants/constants';

import FormLoader from '../../shared-components/FormLoader/FormLoader';
import { ExclamationCircleFill } from 'react-bootstrap-icons';

const ProductManagementDetails = (props: IDetailProps) => {
  const { productStore } = useStore();
  const { GetProductDetailByIdService, productDetailData, getProductDetailState } = productStore;
  const details = productDetailData || {};

  useEffect(() => {
    if (props.selectedRowId > 0) {
      GetProductDetailByIdService(
        Number(getTenantIdFromLocalStorage()),
        props.selectedRowId
      );
    }
  }, [props.selectedRowId]);

  return (
    <>
      {getProductDetailState.inProgress && (
        <FormLoader loading={getProductDetailState.inProgress} />
      )}
      <div className="px-4 py-3">
        <div className="row g-2 d-flex align-items-center">
          <div className="col-auto pt-1 text-muted">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              id="fi_4240765"
              viewBox="0 0 60 60"
              width="32"
              height="32"
            >
              <path d="M30,33A13,13,0,1,0,17,20,13.015,13.015,0,0,0,30,33ZM30,9A11,11,0,1,1,19,20,11.013,11.013,0,0,1,30,9Z"></path>
              <path d="M60,32a2.987,2.987,0,0,0-1.883-2.807L46.3,24.39l2.3-.92a2.115,2.115,0,0,0,1.319-1.742C49.975,21.2,50,20.638,50,20s-.025-1.2-.076-1.718A2.12,2.12,0,0,0,48.6,16.529l-2.591-1.035a16.582,16.582,0,0,0-1.5-3.625L45.6,9.3a2.067,2.067,0,0,0-.3-2.169c-.321-.389-.687-.791-1.166-1.281-.475-.464-.877-.83-1.251-1.139A2.075,2.075,0,0,0,40.7,4.4l-2.569,1.1a16.582,16.582,0,0,0-3.625-1.5L33.47,1.4A2.115,2.115,0,0,0,31.728.077a19.442,19.442,0,0,0-3.446,0A2.12,2.12,0,0,0,26.529,1.4L25.494,3.989a16.582,16.582,0,0,0-3.625,1.5L19.3,4.4a2.069,2.069,0,0,0-2.169.3c-.389.321-.791.687-1.281,1.166-.464.475-.83.877-1.139,1.251A2.075,2.075,0,0,0,14.4,9.3l1.1,2.569a16.582,16.582,0,0,0-1.5,3.625L11.4,16.53a2.115,2.115,0,0,0-1.319,1.742C10.025,18.8,10,19.362,10,20s.025,1.2.076,1.718A2.12,2.12,0,0,0,11.4,23.471l2.3.919L1.88,29.2a3.033,3.033,0,0,0,0,5.612l1.705.693-1.708.7a3.033,3.033,0,0,0,0,5.612l1.705.693-1.708.7a3.033,3.033,0,0,0,0,5.612L28.862,59.775a2.994,2.994,0,0,0,2.275,0L58.12,48.805a3.032,3.032,0,0,0,0-5.611L56.411,42.5l1.709-.7a3.032,3.032,0,0,0,0-5.611L56.411,35.5l1.709-.7A2.985,2.985,0,0,0,60,32ZM12.067,21.53C12.022,21.072,12,20.571,12,20s.022-1.072.068-1.54a.117.117,0,0,1,.073-.073L15.2,17.165a1,1,0,0,0,.6-.688,14.546,14.546,0,0,1,1.668-4.023,1,1,0,0,0,.063-.908L16.235,8.515l0-.111c.285-.344.615-.707,1.024-1.125.433-.424.8-.754,1.14-1.039,0,0,.106,0,.111,0l3.031,1.294a1,1,0,0,0,.908-.063A14.546,14.546,0,0,1,26.477,5.8a1,1,0,0,0,.688-.6l1.221-3.055a.116.116,0,0,1,.084-.076,17.487,17.487,0,0,1,3.07,0,.117.117,0,0,1,.073.073L32.835,5.2a1,1,0,0,0,.688.6,14.546,14.546,0,0,1,4.023,1.668,1,1,0,0,0,.908.063l3.031-1.294.111,0c.344.285.707.615,1.125,1.024.424.433.754.8,1.039,1.14,0,.005,0,.106.005.111l-1.294,3.031a1,1,0,0,0,.063.908A14.546,14.546,0,0,1,44.2,16.477a1,1,0,0,0,.6.688l3.055,1.221a.116.116,0,0,1,.076.084c.045.458.067.959.067,1.53s-.022,1.072-.068,1.54a.117.117,0,0,1-.073.073L44.8,22.835a1,1,0,0,0-.6.688,14.546,14.546,0,0,1-1.668,4.023,1,1,0,0,0-.063.908l1.294,3.031-.005.111c-.285.344-.615.707-1.024,1.125-.433.424-.8.754-1.14,1.039,0,0-.106,0-.111.005l-3.031-1.294a1,1,0,0,0-.908.063A14.546,14.546,0,0,1,33.523,34.2a1,1,0,0,0-.688.6l-1.221,3.055a.116.116,0,0,1-.084.076,17.487,17.487,0,0,1-3.07,0,.117.117,0,0,1-.073-.073L27.165,34.8a1,1,0,0,0-.688-.6,14.546,14.546,0,0,1-4.023-1.668,1,1,0,0,0-.908-.063l-3.031,1.294L18.4,33.76c-.344-.285-.707-.615-1.125-1.024-.424-.433-.754-.8-1.039-1.14,0,0,0-.106,0-.111l1.294-3.031a1,1,0,0,0-.063-.908A14.546,14.546,0,0,1,15.8,23.523a1,1,0,0,0-.6-.688l-3.055-1.221A.116.116,0,0,1,12.067,21.53ZM57.36,45.045a1.032,1.032,0,0,1,0,1.908L30.382,57.924a1.013,1.013,0,0,1-.765,0L2.64,46.955a1.033,1.033,0,0,1,0-1.909l3.605-1.467,22.62,9.2a2.994,2.994,0,0,0,2.275,0l22.621-9.2Zm0-7a1.032,1.032,0,0,1,0,1.908L30.382,50.924a1.013,1.013,0,0,1-.765,0L2.64,39.955a1.033,1.033,0,0,1,0-1.909l3.605-1.467,22.62,9.2a2.994,2.994,0,0,0,2.275,0l22.621-9.2Zm0-5.092L30.382,43.924a1.013,1.013,0,0,1-.765,0L2.64,32.955a1.032,1.032,0,0,1,0-1.908l11.937-4.853a16.786,16.786,0,0,0,.919,1.937L14.4,30.7a2.067,2.067,0,0,0,.3,2.169c.321.389.687.791,1.166,1.281.475.464.877.83,1.251,1.139A2.079,2.079,0,0,0,19.3,35.6l2.569-1.1a16.582,16.582,0,0,0,3.625,1.5L26.53,38.6a2.115,2.115,0,0,0,1.742,1.319C28.8,39.975,29.362,40,30,40s1.2-.025,1.718-.076A2.12,2.12,0,0,0,33.471,38.6l1.035-2.591a16.582,16.582,0,0,0,3.625-1.5L40.7,35.6a2.069,2.069,0,0,0,2.169-.3c.389-.321.791-.687,1.281-1.166.464-.475.83-.877,1.139-1.251A2.075,2.075,0,0,0,45.6,30.7l-1.1-2.569a16.622,16.622,0,0,0,.919-1.937L57.36,31.045a1.032,1.032,0,0,1,0,1.908Z"></path>
              <path d="M24,23a1,1,0,0,0,.707-.293l2-2a1,1,0,0,0-1.414-1.414l-.246.246A4.989,4.989,0,0,1,30,15a1,1,0,0,0,0-2,7,7,0,0,0-6.98,6.606l-.313-.313a1,1,0,0,0-1.414,1.414l2,2A1,1,0,0,0,24,23Z"></path>
              <path d="M34.707,20.707l.246-.246A4.989,4.989,0,0,1,30,25a1,1,0,0,0,0,2,7,7,0,0,0,6.98-6.606l.313.313a1,1,0,0,0,1.414-1.414l-2-2a1,1,0,0,0-1.414,0l-2,2a1,1,0,0,0,1.414,1.414Z"></path>
            </svg>
          </div>
          <div className="col-10">
            <p className="title_sidebar">
              <FormattedMessage id={'product_name'} />
              {'--'}
            </p>
            <h3 className="mb-0 d-flex align-items-center gap-1">
              <span>{details.ProductName}</span>
              {Boolean(
                details?.CorrectionRequest === true ||
                (details?.CorrectionRequest && typeof details?.CorrectionRequest === 'object' && !details?.CorrectionRequest?.IsCompleted) ||
                (details?.HasCorrectionRequest && !details?.IsCorrectionCompleted)
              ) && (
                <ExclamationCircleFill
                  className="text-danger"
                  title={formatMessage('correction_request')}
                />
              )}
            </h3>
          </div>
        </div>
        <div className="bg-light rounded px-3 mt-3 py-2">
          <div className="row g-2">
            <div className="col-12">
              <p className="title_sidebar">
                <FormattedMessage id={'customer'} />{' '}
              </p>
              <h3 className="mb-0">{details.Customer || '--'}</h3>
            </div>
            <div className="col-12">
              <p className="title_sidebar">
                <FormattedMessage id={'tray_size'} />{' '}
              </p>
              <h3 className="mb-0">{details.TraySize || '--'}</h3>
            </div>
       
            <div className="col-12">
              <p className="title_sidebar">
                <FormattedMessage id={'katashiki'} />{' '}
              </p>
              <h3 className="mb-0">{details.Katashiki || '--'}</h3>
            </div>
            <div className="col-12">
              <p className="title_sidebar">
                <FormattedMessage id={'key_info_1'} />{' '}
              </p>
              <h3 className="mb-0">{details.KeyInfo1 || '--'}</h3>
            </div>
        
            <div className="col-12">
              <p className="title_sidebar">
                <FormattedMessage id={'key_info_2'} />{' '}
              </p>
              <h3 className="mb-0">{details.KeyInfo2 || '--'}</h3>
            </div>
            <div className="col-12">
              <p className="title_sidebar">
                <FormattedMessage id={'key_info_3'} />{' '}
              </p>
              <h3 className="mb-0">{details.KeyInfo3 || '--'}</h3>
            </div>
          </div>
        </div>

        <div className="row g-2 mt-4 d-flex align-items-center">
          
              <div className="col-6">
            <p className="title_sidebar">
              <FormattedMessage id={'created_by'} />{' '}
            </p>
            <h3 className="mb-0">{details.CreatedBy || '--'}</h3>
          </div>

       
               <div className="col-6">
            <p className="title_sidebar">
              <FormattedMessage id={'created_date'} />{' '}
            </p>
            <h3 className="mb-0">
              {details.CreatedOn
                ? fixTimezoneOffsetYYYYMMDD(details.CreatedOn)
                : '--'}
            </h3>
          </div>
     
       
          <div className="col-6">
            <p className="title_sidebar">
              <FormattedMessage id={'updated_by'} />{' '}
            </p>
            <h3 className="mb-0">{details.UpdatedBy || '--'}</h3>
          </div>          
           <div className="col-6">
            <p className="title_sidebar">
              <FormattedMessage id={'updated_date'} />{' '}
            </p>
            <h3 className="mb-0">
              {details.UpdatedOn && !details.UpdatedOn.startsWith(minDate.slice(0, 10))
                ? fixTimezoneOffsetYYYYMMDD(details.UpdatedOn)
                : '--'}
            </h3>
          </div>
        </div>

        <div className="row g-2 mt-4 d-flex align-items-center">
          <div className="col-auto pt-1 text-muted">
            <svg
              version="1.1"
              id="fi_134808"
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              x="0px"
              fill="currentColor"
              y="0px"
              viewBox="0 0 60 60"
            >
              <g>
                <path d="M10,25.465h13c0.553,0,1-0.448,1-1s-0.447-1-1-1H10c-0.553,0-1,0.448-1,1S9.447,25.465,10,25.465z"></path>
                <path d="M36,29.465H10c-0.553,0-1,0.448-1,1s0.447,1,1,1h26c0.553,0,1-0.448,1-1S36.553,29.465,36,29.465z"></path>
                <path d="M36,35.465H10c-0.553,0-1,0.448-1,1s0.447,1,1,1h26c0.553,0,1-0.448,1-1S36.553,35.465,36,35.465z"></path>
                <path
                  d="M54.072,2.535L19.93,2.465c-3.27,0-5.93,2.66-5.93,5.93v5.124l-8.07,0.017c-3.27,0-5.93,2.66-5.93,5.93v21.141
		c0,3.27,2.66,5.929,5.93,5.929H12v10c0,0.413,0.254,0.784,0.64,0.933c0.117,0.045,0.239,0.067,0.36,0.067
		c0.276,0,0.547-0.115,0.74-0.327l9.704-10.675l16.626-0.068c3.27,0,5.93-2.66,5.93-5.929v-0.113l5.26,5.786
		c0.193,0.212,0.464,0.327,0.74,0.327c0.121,0,0.243-0.022,0.36-0.067c0.386-0.149,0.64-0.52,0.64-0.933v-10h1.07
		c3.27,0,5.93-2.66,5.93-5.929V8.465C60,5.196,57.341,2.536,54.072,2.535z M44,40.536c0,2.167-1.763,3.929-3.934,3.929l-17.07,0.07
		c-0.28,0.001-0.548,0.12-0.736,0.327L14,53.949v-8.414c0-0.552-0.447-1-1-1H5.93c-2.167,0-3.93-1.763-3.93-3.929V19.465
		c0-2.167,1.763-3.93,3.932-3.93l9.068-0.019c0,0,0,0,0,0c0.001,0,0.001,0,0.002,0l25.068-0.052c2.167,0,3.93,1.763,3.93,3.93
		v18.441V40.536z M58,29.606c0,2.167-1.763,3.929-3.93,3.929H52c-0.553,0-1,0.448-1,1v8.414l-5-5.5V19.395
		c0-3.27-2.66-5.93-5.932-5.93L16,13.514v-5.12c0-2.167,1.763-3.93,3.928-3.93l34.141,0.07c0.001,0,0.001,0,0.002,0
		c2.167,0,3.93,1.763,3.93,3.93V29.606z"
                ></path>
              </g>
            </svg>
          </div>
          <div className="col-10">
            <p className="title_sidebar">
              <FormattedMessage id={'comment'} />{' '}
            </p>
            <h3 className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{details.Remarks || '--'}</h3>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(ProductManagementDetails);
